import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import useAuth from '../../../../../hooks/useAuth';
import CellTowerIcon from '@mui/icons-material/CellTower';
import useAxiosProtect from '../../../../../hooks/useAxiosProtect';

const PIN_TYPE_LABELS = {
    water: 'Water Meter',
    ct: 'Current Transducer',
    gas: 'Gas Sensor',
    hub: 'Hub',
    temperature: 'Temperature Sensor',
};

const PIN_COLORS = {
    water: '#2196F3',
    ct: '#FFC107',
    gas: '#4CAF50',
    hub: '#e61515',
    temperature: '#F44336',
    pending: '#9E9E9E',
};

const PIN_SIZE = 14;
const HUB_PIN_SIZE = PIN_SIZE * 2;

const POPUP_WIDTH = 230;
const POPUP_MAX_HEIGHT = 360;

const SensorLayout = props => {
    const [imageFile, setImageFile] = useState(null);
    const [svgContent, setSvgContent] = useState('');
    const [layoutUrl, setLayoutUrl] = useState('');
    const [flowies, setFlowies] = useState([]);
    const [sensors, setSensors] = useState([]);
    const [noSensorMessage, setNoSensorMessage] = useState('');
    const [centricaFieldValue, setCentricaFieldValue] = useState('');
    const [centricaFieldErr, setCentricaFieldErr] = useState('');
    
    useAuth();
    const [gasUnits, setGasUnits] = useState([]);
    const [hubs, setHubs] = useState([]);
    const [tempSensors, setTempSensors] = useState([]);
    const axiosProtect = useAxiosProtect();
    const init = useRef(true);
    const centricaName = useRef('');

    const {auth, persist} = useAuth();
    const [siteID, setSiteID] = useState('');
    const [mappingMode, setMappingMode] = useState(null); // null | 'sensors' | 'hubs'
    const [pins, setPins] = useState([]);
    const [savedMappings, setSavedMappings] = useState([]);
    const [pendingPin, setPendingPin] = useState(null); // { x, y, clientX, clientY }

    const fileInputRef = useRef(null);

    const handleClick = () => fileInputRef.current.click();
    
    
    
    const uploadLayout = async (svgString = svgContent) => {
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
        const svgFile = new File([svgBlob], 'layout.svg', { type: 'image/svg+xml' });

        const formData = new FormData()
        formData.append("file", svgFile);
        formData.append("upload_preset", "stewwi")
        formData.append("cloud_name", process.env.REACT_APP_CLOUDINARY)

        const res = await fetch("https://api.cloudinary.com/v1_1/dw9sj7d5w/image/upload", {
            method: "POST",
            body: formData
        })

        const uploaded = await res.json()

        let payload = {
            file: uploaded.url,
            id: siteID,
        };

        axiosProtect.post('/s/sites/add-site-layout', payload).then(res => {
            
            
        }).catch(err => console.log(err));
    }
    const handleImageClick = (e) => {
        if (!mappingMode || pendingPin) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setPendingPin({ x, y, clientX: e.clientX, clientY: e.clientY });
    };

    const handleSensorSelect = (sensor, type) => {
        setPins(prev => [...prev, { x: pendingPin.x, y: pendingPin.y, sensor, type }]);
        setPendingPin(null);
    };

    const handleCancelPicker = () => setPendingPin(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            convertImageToSvg(file);
        }
    };


    const convertImageToSvg = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const dataURL = canvas.toDataURL();
                const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="${img.width}" height="${img.height}"><image href="${dataURL}" width="${img.width}" height="${img.height}" /></svg>`;
                setSvgContent(svgString);
                uploadLayout(svgString);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        const controller = new AbortController();
        let site_id = parseInt(window.location.href.split('/').slice(-1));
        if (site_id) setSiteID(site_id);

        const getInfo = async () => {
            try {
                const response = await axiosProtect.get(`/s/sites/get-site-layout?id=${site_id}`);
                if (response.data.site_layout) setLayoutUrl(response.data.site_layout);
            } catch (err) {
                console.log(err);
            }
        }

        const getSensorMappings = async () => {
            try {
                const response = await axiosProtect.get(`/s/sites/get-sensor-svg-data?id=${site_id}`);
                setSavedMappings(response.data.sensor_svg || []);
            } catch (err) {
                console.log(err);
            }
        };

        const getFlowies = async () => {
            try {
                const response = await axiosProtect.get(`/v2s/v2-sensors?site_id=${site_id}&utility=water`, { signal: controller.signal });
                setFlowies(response.data.sensors);
            } catch (err) {
                console.log(err);
            }
        };

        const getCentricaData = async () => {
            try {
                let response = await axiosProtect.get(`/v2s/v2-sensors?site_id=${site_id}&utility=electricity`);
                if (response.status !== 200) { setNoSensorMessage('An error occurred'); return; }
                const centricaSiteName = response.data.sensors.find(sensor => sensor.centrica_site_name)?.centrica_site_name || '';
                centricaName.current = centricaSiteName;
                setCentricaFieldValue(centricaSiteName);
                if (response.data.sensors.length === 0) { setNoSensorMessage('No sensors available'); return; }
                response.data.sensors.sort((a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1);
                setSensors(response.data.sensors);
            } catch (err) {
                setNoSensorMessage('An error occurred');
            }
        };

        const getGasSensor = async () => {

            if(auth?.accessToken){
                axiosProtect.get(`/v2s/v2-sensors?site_id=${site_id}&utility=gas`).then(response => {
                    let sensors = [];
                            
                    for(let i = 0;i<response.data.sensors.length;++i){
                        if(response.data.sensors[i].unit_type !== 'kyz'){
                            sensors.push(response.data.sensors[i]);
                            continue;
                        }
            
                        let sensor = {...response.data.sensors[i]};
                        sensor.calibrationEditOn = false;
                        sensor.calibrationValue = sensor.calibration_factor.toString();
                        sensor.calibrationError = '';
                        sensors.push(sensor);
                    }
            
                    setGasUnits(sensors);
                });
            }

        };

        const getHubs = async () => {
            try {
                const response = await axiosProtect.get(`/v2s/v2-sensors/hubs?site_id=${site_id}`, { signal: controller.signal });
                const hubList = [...(response.data.hubs || [])];
                hubList.sort((a, b) => a.name.localeCompare(b.name));
                setHubs(hubList);
            } catch (err) {
                if (err.name !== 'CanceledError') console.log(err);
            }
        };

        const getTempSensors = async () => {
            try {
                const response = await axiosProtect.get(`/v2s/v2-sensors?site_id=${site_id}&utility=temperature`, { signal: controller.signal });
                const tempList = [...(response.data.sensors || [])];
                tempList.sort((a, b) => a.name.localeCompare(b.name));
                setTempSensors(tempList);
            } catch (err) {
                if (err.name !== 'CanceledError') console.log(err);
            }
        };

        if (init.current) {
            init.current = false;
            getInfo();
            getSensorMappings();
            getFlowies();
            getCentricaData();
            getGasSensor();
            getHubs();
            getTempSensors();
        }
    }, []);

    useEffect(() => {
        const availableSensors = [
            ...flowies.map(sensor => ({ sensor, type: 'water' })),
            ...sensors.map(sensor => ({ sensor, type: 'ct' })),
            ...gasUnits.map(sensor => ({ sensor, type: 'gas' })),
            ...hubs.map(sensor => ({ sensor, type: 'hub' })),
            ...tempSensors.map(sensor => ({ sensor, type: 'temperature' })),
        ];

        setPins(savedMappings.flatMap(mapping => {
            // Hubs live in a separate table, so match on type as well to avoid id collisions
            const match = availableSensors.find(({ sensor, type }) =>
                sensor.id === mapping.sensor_id && (!mapping.type || mapping.type === type)
            );
            return match ? [{
                x: mapping.svg_x,
                y: mapping.svg_y,
                sensor: match.sensor,
                type: match.type,
            }] : [];
        }));
    }, [savedMappings, flowies, sensors, gasUnits, hubs, tempSensors]);

    const handleDoneMapping = () => {
        if (pins.length > 0) {
            const payload = pins.map(pin => ({
                sensor_id: pin.sensor.id,
                site_id: siteID,
                type: pin.type,
                svg_x: pin.x,
                svg_y: pin.y,
            }));
            
            axiosProtect.post('/s/sites/add-sensor-svg-data', payload)
                .then(res => console.log('Pins saved', res))
                .catch(err => console.log(err));
        }
        setMappingMode(null);
        setPendingPin(null);
    };

    const hasWaterMeters = Array.isArray(flowies) && flowies.length > 0;
    const hasCTs = sensors.length > 0;
    const hasGasSensors = gasUnits.length > 0;
    const hasHubs = hubs.length > 0;
    const hasTempSensors = tempSensors.length > 0;
    const isHubMapping = mappingMode === 'hubs';

    // Clamp popup so it doesn't render off-screen
    const popupLeft = pendingPin ? Math.min(pendingPin.clientX + 10, window.innerWidth - POPUP_WIDTH - 8) : 0;
    const popupTop = pendingPin ? Math.min(pendingPin.clientY + 10, window.innerHeight - POPUP_MAX_HEIGHT - 8) : 0;

    return (
        <>
            {!layoutUrl && !imageFile && <Button onClick={handleClick}>Upload Site Layout</Button>}
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />

            <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
                {(imageFile || layoutUrl) && (() => {
                    const imageSrc = imageFile ? URL.createObjectURL(imageFile) : layoutUrl;
                    return (
                        <div className="mb-3" style={{ flex: '0 0 60%' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {mappingMode !== 'hubs' && (
                                    <Button
                                        onClick={() => mappingMode ? handleDoneMapping() : setMappingMode('sensors')}
                                        variant={mappingMode ? 'contained' : 'outlined'}
                                    >
                                        {mappingMode ? 'Done Mapping' : 'Map Sensors'}
                                    </Button>
                                )}
                                {mappingMode !== 'sensors' && (
                                    <Button
                                        onClick={() => mappingMode ? handleDoneMapping() : setMappingMode('hubs')}
                                        variant={mappingMode ? 'contained' : 'outlined'}
                                        sx={mappingMode ? {
                                            backgroundColor: PIN_COLORS.hub,
                                            '&:hover': { backgroundColor: PIN_COLORS.hub, filter: 'brightness(0.9)' },
                                        } : {
                                            color: PIN_COLORS.hub,
                                            borderColor: PIN_COLORS.hub,
                                            '&:hover': { borderColor: PIN_COLORS.hub, backgroundColor: `${PIN_COLORS.hub}14` },
                                        }}
                                    >
                                        {mappingMode ? 'Done Mapping Hubs' : 'Map Hubs'}
                                    </Button>
                                )}
                            </div>
                            {/* Click handler is on the wrapper so clicks landing on an existing (e.g. large hub) pin still place a new pin */}
                            <div
                                style={{ position: 'relative', display: 'block', width: '100%', marginTop: 8, cursor: mappingMode ? 'crosshair' : 'default' }}
                                onClick={handleImageClick}
                            >
                                <img
                                    className="img-fluid"
                                    src={imageSrc}
                                    style={{ display: 'block', width: '100%' }}
                                    alt="site layout"
                                />
                                {pins.map((pin, i) => (
                                    <Pin key={i} pin={pin} />
                                ))}
                                {pendingPin && isHubMapping && (
                                    <CellTowerIcon
                                        style={{
                                            position: 'absolute',
                                            left: `${pendingPin.x}%`,
                                            top: `${pendingPin.y}%`,
                                            fontSize: HUB_PIN_SIZE,
                                            color: PIN_COLORS.pending,
                                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                                            transform: 'translate(-50%, -50%)',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                )}
                                {pendingPin && !isHubMapping && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: `${pendingPin.x}%`,
                                            top: `${pendingPin.y}%`,
                                            width: PIN_SIZE,
                                            height: PIN_SIZE,
                                            borderRadius: '50%',
                                            background: PIN_COLORS.pending,
                                            border: '2px solid white',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
                                            transform: 'translate(-50%, -50%)',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })()}

                <div style={{ flex: 1 }}>
                    {hasWaterMeters && (
                        <div style={{ marginBottom: 24 }}>
                            <strong>Water Meters</strong>
                            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                {flowies.map((flowie, i) => (
                                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: PIN_COLORS.water, display: 'inline-block', flexShrink: 0 }} />
                                        {flowie.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {hasCTs && (
                        <div style={{ marginBottom: hasGasSensors ? 24 : 0 }}>
                            <strong>Current Transducers</strong>
                            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                {sensors.map((sensor, i) => (
                                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: PIN_COLORS.ct, display: 'inline-block', flexShrink: 0 }} />
                                        {sensor.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {hasGasSensors && (
                        <div>
                            <strong>Gas Sensors</strong>
                            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                {gasUnits.map((unit, i) => (
                                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: PIN_COLORS.gas, display: 'inline-block', flexShrink: 0 }} />
                                        {unit.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {(hasHubs || hasTempSensors) && (
                    <div style={{ flex: 1 }}>
                        {hasHubs && (
                            <div style={{ marginBottom: hasTempSensors ? 24 : 0 }}>
                                <strong>Hubs</strong>
                                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                    {hubs.map((hub, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <CellTowerIcon style={{ fontSize: 16, color: PIN_COLORS.hub, flexShrink: 0 }} />
                                            {hub.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {hasTempSensors && (
                            <div>
                                <strong>Temperature Sensors</strong>
                                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                    {tempSensors.map((unit, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: PIN_COLORS.temperature, display: 'inline-block', flexShrink: 0 }} />
                                            {unit.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {pendingPin && (
                <div
                    onClick={e => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        top: popupTop,
                        left: popupLeft,
                        background: 'white',
                        border: '1px solid #ddd',
                        borderRadius: 8,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                        zIndex: 9999,
                        width: POPUP_WIDTH,
                        maxHeight: POPUP_MAX_HEIGHT,
                        overflowY: 'auto',
                        padding: '12px 0',
                    }}
                >
                    <div style={{ padding: '0 16px 10px', fontWeight: 600, fontSize: 14, borderBottom: '1px solid #eee', marginBottom: 4 }}>
                        {isHubMapping ? 'Assign Hub' : 'Assign Sensor'}
                    </div>
                    {!isHubMapping && hasWaterMeters && (
                        <>
                            <div style={{ padding: '8px 16px 3px', fontSize: 11, color: '#999', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                Water Meters
                            </div>
                            {flowies.map((flowie, i) => (
                                <PickerItem
                                    key={i}
                                    name={flowie.name}
                                    color={PIN_COLORS.water}
                                    hoverBg="#f0f7ff"
                                    onClick={() => handleSensorSelect(flowie, 'water')}
                                />
                            ))}
                        </>
                    )}

                    {!isHubMapping && hasCTs && (
                        <>
                            <div style={{ padding: '8px 16px 3px', fontSize: 11, color: '#999', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: hasWaterMeters ? 6 : 0 }}>
                                Current Transducers
                            </div>
                            {sensors.map((sensor, i) => (
                                <PickerItem
                                    key={i}
                                    name={sensor.name}
                                    color={PIN_COLORS.ct}
                                    hoverBg="#fffbea"
                                    onClick={() => handleSensorSelect(sensor, 'ct')}
                                />
                            ))}
                        </>
                    )}

                    {!isHubMapping && hasGasSensors && (
                        <>
                            <div style={{ padding: '8px 16px 3px', fontSize: 11, color: '#999', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: (hasWaterMeters || hasCTs) ? 6 : 0 }}>
                                Gas Sensors
                            </div>
                            {gasUnits.map((unit, i) => (
                                <PickerItem
                                    key={i}
                                    name={unit.name}
                                    color={PIN_COLORS.gas}
                                    hoverBg="#f0fff4"
                                    onClick={() => handleSensorSelect(unit, 'gas')}
                                />
                            ))}
                        </>
                    )}

                    {!isHubMapping && hasTempSensors && (
                        <>
                            <div style={{ padding: '8px 16px 3px', fontSize: 11, color: '#999', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: (hasWaterMeters || hasCTs || hasGasSensors) ? 6 : 0 }}>
                                Temperature Sensors
                            </div>
                            {tempSensors.map((unit, i) => (
                                <PickerItem
                                    key={i}
                                    name={unit.name}
                                    color={PIN_COLORS.temperature}
                                    hoverBg="#fff0f0"
                                    onClick={() => handleSensorSelect(unit, 'temperature')}
                                />
                            ))}
                        </>
                    )}

                    {isHubMapping && hasHubs && (
                        <>
                            <div style={{ padding: '8px 16px 3px', fontSize: 11, color: '#999', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                Hubs
                            </div>
                            {hubs.map((hub, i) => (
                                <PickerItem
                                    key={i}
                                    name={hub.name}
                                    color={PIN_COLORS.hub}
                                    hoverBg="#f8f0fb"
                                    onClick={() => handleSensorSelect(hub, 'hub')}
                                />
                            ))}
                        </>
                    )}

                    {!isHubMapping && !hasWaterMeters && !hasCTs && !hasGasSensors && !hasTempSensors && (
                        <div style={{ padding: '10px 16px', color: '#999', fontSize: 13 }}>No sensors available</div>
                    )}

                    {isHubMapping && !hasHubs && (
                        <div style={{ padding: '10px 16px', color: '#999', fontSize: 13 }}>No hubs available</div>
                    )}

                    <div style={{ borderTop: '1px solid #eee', marginTop: 6 }}>
                        <PickerItem name="Cancel" color={null} hoverBg="#f5f5f5" onClick={handleCancelPicker} style={{ color: '#888', fontSize: 13 }} />
                    </div>
                </div>
            )}
        </>
    );
};

const Pin = ({ pin }) => {
    const [hovered, setHovered] = useState(false);
    const isHub = pin.type === 'hub';
    const size = isHub ? HUB_PIN_SIZE : PIN_SIZE;
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'absolute',
                left: `${pin.x}%`,
                top: `${pin.y}%`,
                transform: 'translate(-50%, -50%)',
                cursor: 'pointer',
                // Hubs always sit beneath sensor pins so the smaller pins stay visible and hoverable
                zIndex: isHub ? 0 : (hovered ? 10 : 1),
            }}
        >
            {hovered && (
                <div style={{
                    position: 'absolute',
                    bottom: size + 6,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(33,33,33,0.92)',
                    color: '#fff',
                    borderRadius: 6,
                    padding: '5px 10px',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontWeight: 700, fontSize: 11, opacity: 0.7, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {PIN_TYPE_LABELS[pin.type]}
                    </div>
                    {pin.sensor.name}
                </div>
            )}
            {isHub ? (
                <CellTowerIcon style={{
                    display: 'block',
                    fontSize: size,
                    color: PIN_COLORS.hub,
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                }} />
            ) : (
                <div style={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    background: PIN_COLORS[pin.type],
                    border: '2px solid white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
                }} />
            )}
        </div>
    );
};

const PickerItem = ({ name, color, hoverBg, onClick, style = {} }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: hovered ? hoverBg : 'transparent',
                fontSize: 14,
                ...style,
            }}
        >
            {color && <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />}
            {name}
        </div>
    );
};

export default SensorLayout;